import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  Activity,
  Clock,
  Gauge,
  ListChecks,
  RefreshCw,
  Server,
  Timer,
  Wifi,
  WifiOff,
  Zap,
} from 'lucide-react';
import { Header } from '../../Components/Header/Header';
import { FreshnessBadge } from '../../Components/Vending/FreshnessBadge';
import {
  VendingKpiCard,
  VendingSectionCard,
  VendingSectionTitle,
  compactDialogTableSx,
} from '../../Components/Vending/VendingStyles';
import vendingApiClient from '../../Services/VendingApi';
import { formatUTC } from '../../Services/FormatUtc';
import { PageContainer } from '../../Styles/AppStyles';
import { COLORS } from '../../Styles/Colors';
import {
  IntegrationConnectionStatus,
  IntegrationHealthData,
  IntegrationHealthResponse,
  IntegrationQuotaCompany,
  SyncResourceStatus,
  VendingSyncLogListResponse,
  VendingSyncLogRow,
} from '../../Types/vending';

const DISMISSED_ERRORS_KEY = 'vending-integration-dismissed-errors';

const headCellSx = {
  py: 1.25,
  px: 2,
  fontWeight: 700,
  fontSize: '0.6875rem',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.05em',
  color: COLORS.lightGray,
  backgroundColor: '#f8f9fa',
  borderBottom: `1px solid ${COLORS.lightGray}35`,
  whiteSpace: 'nowrap' as const,
};

const bodyCellSx = {
  py: 1.5,
  px: 2,
  borderBottom: `1px solid ${COLORS.lightGray}30`,
  verticalAlign: 'middle' as const,
  fontSize: '0.8125rem',
};

const connectionLabel = (status: IntegrationConnectionStatus) => {
  switch (status) {
    case 'connected': return 'Connected';
    case 'syncing': return 'Syncing';
    case 'degraded': return 'Degraded';
    case 'stale': return 'Stale';
    case 'error': return 'Error';
    default: return 'Unknown';
  }
};

const connectionColor = (status: IntegrationConnectionStatus) => {
  switch (status) {
    case 'connected': return COLORS.success;
    case 'syncing': return COLORS.teal;
    case 'degraded':
    case 'stale': return COLORS.orange;
    case 'error': return COLORS.red;
    default: return COLORS.lightGray;
  }
};

const syncStatusColor = (status: SyncResourceStatus | string) => {
  switch (status) {
    case 'success': return COLORS.success;
    case 'running': return COLORS.teal;
    case 'warning': return COLORS.orange;
    case 'failed': return COLORS.red;
    default: return COLORS.lightGray;
  }
};

const loadDismissedErrorIds = (): number[] => {
  try {
    const raw = localStorage.getItem(DISMISSED_ERRORS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map(Number).filter(Number.isFinite) : [];
  } catch {
    return [];
  }
};

const StatusChip = ({ status, color }: { status: string; color: string }) => (
  <Chip
    label={status}
    size="small"
    sx={{
      height: 24,
      fontWeight: 700,
      fontSize: '0.6875rem',
      textTransform: 'capitalize',
      color,
      backgroundColor: `${color}14`,
      border: 'none',
      '& .MuiChip-label': { px: 1 },
    }}
  />
);

interface KpiCardProps {
  label: string;
  value: string | number;
  sublabel?: string;
  icon: React.ReactNode;
  accent?: string;
}

const KpiCard = ({ label, value, sublabel, icon, accent = COLORS.teal }: KpiCardProps) => (
  <VendingKpiCard>
    <CardContent sx={{ py: 2.5, '&:last-child': { pb: 2.5 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
        <Typography
          variant="body2"
          sx={{
            color: COLORS.lightGray,
            fontWeight: 700,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            fontSize: '0.7rem',
          }}
        >
          {label}
        </Typography>
        <Box sx={{ color: accent, opacity: 0.9 }}>{icon}</Box>
      </Box>
      <Typography variant="h4" fontWeight="bold" sx={{ color: accent, lineHeight: 1.15, fontSize: { xs: '1.5rem', md: '1.75rem' } }}>
        {value}
      </Typography>
      {sublabel && (
        <Typography variant="body2" sx={{ mt: 0.75, color: COLORS.lightGray, fontSize: '0.8125rem' }}>
          {sublabel}
        </Typography>
      )}
    </CardContent>
  </VendingKpiCard>
);

const QuotaBar = ({ company }: { company: IntegrationQuotaCompany }) => {
  const pct = company.limit > 0 ? Math.min(100, Math.round((company.used / company.limit) * 100)) : 0;
  const accent = company.blocked
    ? COLORS.red
    : pct >= 85
      ? COLORS.orange
      : COLORS.teal;

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: `1px solid ${COLORS.lightGray}40`,
        backgroundColor: company.blocked ? `${COLORS.red}06` : '#fafbfc',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1, mb: 1 }}>
        <Typography fontWeight={700} sx={{ color: COLORS.darkGray, fontSize: '0.875rem' }}>
          {company.name}
        </Typography>
        <Typography variant="caption" sx={{ color: accent, fontWeight: 700, whiteSpace: 'nowrap' }}>
          {company.used} / {company.limit}
          {company.blocked ? ' · blocked' : ''}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          height: 8,
          borderRadius: 4,
          backgroundColor: `${accent}18`,
          mb: 1,
          '& .MuiLinearProgress-bar': { backgroundColor: accent, borderRadius: 4 },
        }}
      />
      <Typography variant="caption" sx={{ color: COLORS.lightGray, display: 'block' }}>
        Manual left {company.manual_remaining} · Flex left {company.flex_remaining}
        {' · '}Resets {formatUTC(company.resets_at)} UTC
      </Typography>
    </Box>
  );
};

export const VendingIntegrationHealthPage = () => {
  const [health, setHealth] = useState<IntegrationHealthData | null>(null);
  const [asOf, setAsOf] = useState<string | null>(null);
  const [logs, setLogs] = useState<VendingSyncLogRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [logsLoading, setLogsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<{ text: string; severity: 'success' | 'error' | 'info' } | null>(null);
  const [triggering, setTriggering] = useState<string | null>(null);
  const [logStatusFilter, setLogStatusFilter] = useState('');
  const [logTypeFilter, setLogTypeFilter] = useState('');
  const [dismissedErrorIds, setDismissedErrorIds] = useState<number[]>(() => loadDismissedErrorIds());

  const dismissError = (id: number) => {
    setDismissedErrorIds((prev) => {
      const next = prev.includes(id) ? prev : [...prev, id];
      localStorage.setItem(DISMISSED_ERRORS_KEY, JSON.stringify(next));
      return next;
    });
  };

  const visibleRecentErrors = useMemo(() => {
    if (!health?.recent_errors?.length) return [];
    return health.recent_errors
      .filter((errRow) => !dismissedErrorIds.includes(errRow.id))
      .slice(0, 3);
  }, [health?.recent_errors, dismissedErrorIds]);

  const loadHealth = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await vendingApiClient('/integration/health');
      const json: IntegrationHealthResponse = await response.json();
      setHealth(json.data);
      setAsOf(json.meta?.as_of ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load integration health');
      setHealth(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const params = new URLSearchParams({ limit: '50' });
      if (logStatusFilter) params.set('status', logStatusFilter);
      if (logTypeFilter) params.set('sync_type', logTypeFilter);
      const response = await vendingApiClient(`/integration/sync-logs?${params.toString()}`);
      const json: VendingSyncLogListResponse = await response.json();
      setLogs(json.data);
    } catch {
      setLogs([]);
    } finally {
      setLogsLoading(false);
    }
  }, [logStatusFilter, logTypeFilter]);

  useEffect(() => {
    loadHealth();
  }, [loadHealth]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const triggerSync = async (resource: string) => {
    setTriggering(resource);
    setMessage(null);
    try {
      const response = await vendingApiClient(`/integration/sync/${resource}`, { method: 'POST' });
      const json = await response.json();
      const already = json.data?.already_queued;
      setMessage({
        text: already
          ? `${resource} sync already queued (job ${json.data?.job_id}).`
          : `${resource} sync queued (job ${json.data?.job_id}).`,
        severity: 'info',
      });
      await loadHealth();
      await loadLogs();
    } catch (err) {
      setMessage({
        text: err instanceof Error ? err.message : 'Failed to queue sync',
        severity: 'error',
      });
    } finally {
      setTriggering(null);
    }
  };

  const provider = health?.provider;
  const quota = health?.quota ?? [];
  const providerAccent = provider ? connectionColor(provider.status) : COLORS.teal;

  const subtitle = useMemo(() => {
    if (!provider) return 'Vendor connection and sync status';
    const parts = [`Provider ${connectionLabel(provider.status)}`];
    if (provider.last_successful_connection) {
      parts.push(`Last success ${formatUTC(provider.last_successful_connection)} UTC`);
    }
    return parts.join(' · ');
  }, [provider]);

  return (
    <PageContainer>
      <Header title="Integration Health" subtitle={subtitle}>
        <FreshnessBadge asOf={asOf} />
        <Button
          variant="outlined"
          startIcon={<RefreshCw size={16} />}
          onClick={() => { loadHealth(); loadLogs(); }}
          disabled={loading || logsLoading}
          sx={{
            borderColor: COLORS.teal,
            color: COLORS.teal,
            textTransform: 'none',
            '&:hover': { borderColor: COLORS.darkTeal, backgroundColor: `${COLORS.teal}0a` },
          }}
        >
          Refresh
        </Button>
      </Header>

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', px: { xs: 2, md: 3 }, pb: 3, pt: 2 }}>
        {message && (
          <Alert severity={message.severity} sx={{ mb: 2, borderRadius: 2 }} onClose={() => setMessage(null)}>
            {message.text}
          </Alert>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: COLORS.teal }} />
          </Box>
        )}

        {!loading && error && (
          <Alert
            severity="error"
            sx={{ borderRadius: 2 }}
            action={<Button color="inherit" size="small" onClick={loadHealth}>Retry</Button>}
          >
            {error}
          </Alert>
        )}

        {!loading && health && provider && (
          <>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
                gap: 2,
                mb: 3,
              }}
            >
              <KpiCard
                label="Provider API"
                value={connectionLabel(provider.status)}
                sublabel={
                  provider.age_minutes != null
                    ? `${provider.age_minutes} min since last success`
                    : 'No successful sync yet'
                }
                icon={provider.status === 'error' || provider.status === 'unknown'
                  ? <WifiOff size={22} />
                  : <Wifi size={22} />}
                accent={providerAccent}
              />
              <KpiCard
                label="Last Connection"
                value={
                  provider.age_minutes != null
                    ? provider.age_minutes < 60
                      ? `${provider.age_minutes}m`
                      : `${Math.floor(provider.age_minutes / 60)}h`
                    : '—'
                }
                sublabel={
                  provider.last_successful_connection
                    ? `${formatUTC(provider.last_successful_connection)} UTC`
                    : 'No successful sync yet'
                }
                icon={<Clock size={22} />}
                accent={COLORS.teal}
              />
              <KpiCard
                label="API Response"
                value={
                  provider.api_response_time_ms != null
                    ? `${provider.api_response_time_ms} ms`
                    : '—'
                }
                sublabel="Latest vendor call"
                icon={<Timer size={22} />}
                accent={COLORS.teal}
              />
              <KpiCard
                label="Job Queue"
                value={health.jobs.queued + health.jobs.processing}
                sublabel={`${health.jobs.queued} queued · ${health.jobs.processing} running · ${health.jobs.failed} failed`}
                icon={<ListChecks size={22} />}
                accent={health.jobs.failed > 0 ? COLORS.orange : COLORS.teal}
              />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Box sx={{ color: COLORS.teal, display: 'flex' }}><Activity size={14} /></Box>
              <VendingSectionTitle style={{ marginBottom: 0 }}>Sync Status</VendingSectionTitle>
            </Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
                gap: 2,
                mb: 3,
              }}
            >
              {health.resources.map((resource) => {
                const accent = syncStatusColor(resource.status);
                return (
                  <VendingSectionCard key={resource.resource}>
                    <CardContent sx={{ pb: '16px !important', display: 'flex', flexDirection: 'column', height: '100%' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5, gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: 1.5,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: `${accent}14`,
                              color: accent,
                              flexShrink: 0,
                            }}
                          >
                            <Server size={16} />
                          </Box>
                          <Typography fontWeight={700} sx={{ color: COLORS.darkGray }}>
                            {resource.label}
                          </Typography>
                        </Box>
                        <StatusChip status={resource.status} color={accent} />
                      </Box>

                      <Typography variant="body2" sx={{ color: COLORS.lightGray, mb: 0.5, fontSize: '0.8125rem' }}>
                        {resource.last_successful_sync
                          ? `Last success ${formatUTC(resource.last_successful_sync)} UTC`
                          : 'Never synced'}
                      </Typography>
                      <Typography variant="body2" fontWeight={600} sx={{ color: COLORS.darkGray, mb: 1 }}>
                        {resource.records_processed.toLocaleString()} records
                        {resource.records_failed > 0 ? ` · ${resource.records_failed} failed` : ''}
                      </Typography>

                      {resource.last_error && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: COLORS.red,
                            display: 'block',
                            mb: 1.5,
                            lineHeight: 1.4,
                            flex: 1,
                          }}
                        >
                          {resource.last_error}
                        </Typography>
                      )}

                      <Box sx={{ flex: 1 }} />

                      {resource.can_trigger && (
                        <Button
                          size="small"
                          variant="contained"
                          disabled={triggering === resource.resource}
                          startIcon={
                            triggering === resource.resource
                              ? <CircularProgress size={14} color="inherit" />
                              : <Zap size={14} />
                          }
                          onClick={() => triggerSync(resource.resource)}
                          sx={{
                            mt: 1,
                            alignSelf: 'flex-start',
                            bgcolor: COLORS.teal,
                            textTransform: 'none',
                            fontWeight: 600,
                            boxShadow: 'none',
                            minHeight: 32,
                            '&:hover': { bgcolor: COLORS.darkTeal, boxShadow: 'none' },
                          }}
                        >
                          {triggering === resource.resource ? 'Queuing…' : 'Run sync'}
                        </Button>
                      )}
                    </CardContent>
                  </VendingSectionCard>
                );
              })}
            </Box>

            {quota.length > 0 && (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Box sx={{ color: COLORS.teal, display: 'flex' }}><Gauge size={14} /></Box>
                  <VendingSectionTitle style={{ marginBottom: 0 }}>Daily API Quota</VendingSectionTitle>
                </Box>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', xl: 'repeat(3, 1fr)' },
                    gap: 2,
                    mb: 3,
                  }}
                >
                  {quota.map((company) => (
                    <QuotaBar key={company.provider_company_id} company={company} />
                  ))}
                </Box>
              </>
            )}

            {visibleRecentErrors.length > 0 && (
              <>
                <VendingSectionTitle>Recent Errors</VendingSectionTitle>
                <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {visibleRecentErrors.map((errRow) => (
                    <Alert
                      key={errRow.id}
                      severity={errRow.status === 'failed' ? 'error' : 'warning'}
                      sx={{
                        alignItems: 'flex-start',
                        borderRadius: 2,
                        border: `1px solid ${errRow.status === 'failed' ? `${COLORS.red}30` : `${COLORS.orange}40`}`,
                      }}
                      action={(
                        <IconButton
                          aria-label="Dismiss error"
                          size="small"
                          onClick={() => dismissError(errRow.id)}
                          sx={{ color: 'inherit', mt: -0.25 }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      )}
                    >
                      <strong>{errRow.sync_type_label}</strong>
                      {' · '}
                      {formatUTC(errRow.started_at)} UTC
                      {' — '}
                      {errRow.error_message || 'Unknown error'}
                    </Alert>
                  ))}
                </Box>
              </>
            )}
          </>
        )}

        <VendingSectionTitle sx={{ mb: 1.5 }}>
          Sync History
        </VendingSectionTitle>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={logStatusFilter}
              onChange={(e) => setLogStatusFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="success">Success</MenuItem>
              <MenuItem value="warning">Warning</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
              <MenuItem value="running">Running</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Type</InputLabel>
            <Select
              label="Type"
              value={logTypeFilter}
              onChange={(e) => setLogTypeFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="machines">Machines</MenuItem>
              <MenuItem value="inventory">Inventory</MenuItem>
              <MenuItem value="inventory_full">Inventory Full</MenuItem>
              <MenuItem value="transactions">Transactions</MenuItem>
              <MenuItem value="transaction_lines">Transaction Lines</MenuItem>
              <MenuItem value="products">Products</MenuItem>
              <MenuItem value="employees">Employees</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {logsLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} sx={{ color: COLORS.teal }} />
          </Box>
        )}

        {!logsLoading && logs.length === 0 && (
          <Box
            sx={{
              textAlign: 'center',
              py: 6,
              px: 2,
              borderRadius: 2,
              border: `1px dashed ${COLORS.lightGray}`,
              backgroundColor: '#fafbfc',
            }}
          >
            <Typography variant="h6" fontWeight={700} sx={{ color: COLORS.darkGray, mb: 0.5 }}>
              No sync logs yet
            </Typography>
            <Typography variant="body2" sx={{ color: COLORS.lightGray }}>
              Run a sync from the cards above to populate history.
            </Typography>
          </Box>
        )}

        {!logsLoading && logs.length > 0 && (
          <Paper
            elevation={0}
            sx={{
              border: `1px solid ${COLORS.lightGray}40`,
              borderRadius: 2,
              overflow: 'hidden',
              boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
            }}
          >
            <TableContainer>
              <Table size="small" sx={compactDialogTableSx}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={headCellSx}>Time</TableCell>
                    <TableCell sx={headCellSx}>Type</TableCell>
                    <TableCell sx={headCellSx}>Status</TableCell>
                    <TableCell sx={headCellSx} align="right">Records</TableCell>
                    <TableCell sx={headCellSx} align="right">API calls</TableCell>
                    <TableCell sx={headCellSx} align="right">Duration</TableCell>
                    <TableCell sx={headCellSx}>Triggered</TableCell>
                    <TableCell sx={headCellSx}>Error</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log.id} hover>
                      <TableCell sx={bodyCellSx}>{formatUTC(log.started_at)}</TableCell>
                      <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{log.sync_type_label}</TableCell>
                      <TableCell sx={bodyCellSx}>
                        <StatusChip status={log.status} color={syncStatusColor(log.status)} />
                      </TableCell>
                      <TableCell sx={bodyCellSx} align="right">
                        {log.records_received.toLocaleString()}
                        {log.records_failed > 0 && (
                          <Typography component="span" variant="caption" sx={{ color: COLORS.red, ml: 0.5 }}>
                            ({log.records_failed} fail)
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={bodyCellSx} align="right">{log.api_calls}</TableCell>
                      <TableCell sx={bodyCellSx} align="right">
                        {log.duration_ms != null ? `${log.duration_ms} ms` : '—'}
                      </TableCell>
                      <TableCell sx={{ ...bodyCellSx, textTransform: 'capitalize' }}>{log.triggered_by}</TableCell>
                      <TableCell
                        sx={{
                          ...bodyCellSx,
                          maxWidth: 240,
                          color: log.error_message ? COLORS.darkGray : COLORS.lightGray,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={log.error_message || undefined}
                      >
                        {log.error_message || '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Box>
    </PageContainer>
  );
};

export default VendingIntegrationHealthPage;
